import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSessionStore } from '@/features/auth/sessionStore'
import { useLogout } from '@/features/auth/useAuth'
import s from './site.module.css'

/* The signed-in identity menu: who you are, edit your profile, sign out.

   Only students can edit a profile — the backend exposes `PATCH
   /v1/students/me` and nothing equivalent for an admin — so the edit item is
   omitted rather than shown as a link that would 404. */

export function UserMenu(): ReactNode {
  const navigate = useNavigate()
  const logout = useLogout()
  const user = useSessionStore((state) => state.user)

  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  /* A menu that only closes when you hit its own button is a menu that gets
     stuck open. Close on an outside press and on Escape, and put focus back
     on the trigger so keyboard users are not dropped at the top of the page. */
  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent): void {
      if (wrapRef.current?.contains(event.target as Node) === false) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (user === null) return null

  const isStudent = user.user_type === 'student'
  const name = user.student?.name ?? user.admin?.name ?? ''
  const email = user.student?.email ?? user.admin?.email ?? ''
  const initial = name.trim().slice(0, 1).toUpperCase() || '·'

  async function signOut(): Promise<void> {
    setOpen(false)
    await logout()
    // Admins belong on the admin sign-in screen, not the student one.
    navigate(isStudent ? '/login' : '/admin/login', { replace: true })
  }

  return (
    <div className={s.userWrap} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={s.userButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={s.avatar} aria-hidden="true">
          {initial}
        </span>
        <span className={s.userName}>{name}</span>
        <svg className={s.caret} viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true">
          <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="visually-hidden">Account menu for {name}</span>
      </button>

      {open && (
        <div className={s.menu} role="menu">
          <div className={s.menuIdentity}>
            <span className={s.avatar} aria-hidden="true">
              {initial}
            </span>
            <span className={s.menuIdentityText}>
              <span className={s.menuName}>{name}</span>
              <span className={s.menuEmail}>{email}</span>
            </span>
          </div>

          {isStudent && (
            <Link to="/profile" role="menuitem" className={s.menuItem} onClick={() => setOpen(false)}>
              Edit profile
            </Link>
          )}

          <button
            type="button"
            role="menuitem"
            className={[s.menuItem, s.menuItemDanger].join(' ')}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
